import { LocalDateTime } from 'js-joda';
import {
  defaults,
  filter,
  find,
  flatMap,
  flatten,
  forEach,
  groupBy,
  has,
  includes,
  isEmpty,
  map,
  max,
  min,
  pick,
  range,
  some,
  sortBy,
  toNumber,
  toString,
} from 'es-toolkit/compat';
import { oc } from 'ts-optchain';

import * as TableBasedTemplates from 'types/tableBasedTemplates';
import {
  NO_TABLE_OR_GROUP_FOR_INFO,
  NO_TICKET_TYPE_ID_FOR_TURN,
  TURN_DEFAULT_PARTY_SIZES,
} from 'constants/TableBasedTemplatesConstants';
import { NonemptyClosedNumericRange, range as tockRange } from 'utils/range/ranges';
import { TurnTimeRequest, dashboard } from 'types';
import { adjustForBusinessDay } from 'utils/timeUtils';
import { archivedExperienceColor } from 'constants/UIConstants';
import { isOrderVariety } from '@tock/shared/utils/offerings';
import AppSettings from 'settings/AppSettings';
import TableTypeConstants from 'constants/TableTypeConstants';

/* Event ticket types are filtered out, so in the case that one is incorrectly
 * on a template, we need a default.
 */
export const DEFAULT_TICKET_TYPE_SLIM_INFO: TableBasedTemplates.TicketTypeSlimInfo = {
  id: -1,
  colorClassName: 'SquareColor--archived',
  covers: 0,
  defaultPrice: 0,
  ticketTypePriceConfig: [],
  displayColor: archivedExperienceColor,
  fees: [],
  isArchived: true,
  name: 'Unknown',
  shortCode: 'NA',
  supplementGroups: [],
  variety: 'RESERVATION',
};

/**
 * Puts the room and section together in a consistent way
 *
 * @param table The table
 * @returns {string} A string linking the room and the section.
 */
export function getSectionLabelForTable(table: { room: string; section: string }): string {
  return `${table.room} - ${table.section}`;
}

export function getSlotKeyForTemplateInfo(templateInfo: dashboard.SeatingTemplateInfo): string {
  if (templateInfo.tableId) {
    return getSlotKeyForTableOrTableGroup({ type: 'TABLE', id: templateInfo.tableId });
  } else if (templateInfo.tableGroupId) {
    return getSlotKeyForTableOrTableGroup({ type: 'GROUP', id: templateInfo.tableGroupId });
  } else {
    return NO_TABLE_OR_GROUP_FOR_INFO;
  }
}

export function getSlotKeyForTableOrTableGroup(tableOrTableGroup: {
  type: 'GROUP' | 'TABLE' | 'TABLE_COMBINATION';
  id: number;
}): string {
  return `${tableOrTableGroup.type}:${tableOrTableGroup.id}`;
}

/**
 * An object mapping the "size" of the turn time to the relevant part sizes
 */
const SIZE_TO_KNOWN_RANGE_MAP: { [size: number]: string } = {
  2: '1-2',
  4: '3-5',
  6: '6+',
};

export function getPartyRangeStringForTurnTime(turnTime: dashboard.TurnTime): string {
  if (turnTime.isNewStyle) {
    if (turnTime.minPartySize === turnTime.maxPartySize) {
      return `${turnTime.minPartySize}`;
    } else if (turnTime.maxPartySize) {
      return `${turnTime.minPartySize}-${turnTime.maxPartySize}`;
    } else {
      return `${turnTime.minPartySize}+`;
    }
  } else {
    return SIZE_TO_KNOWN_RANGE_MAP[turnTime.maxPartySize];
  }
}

export function getPartyRangeStringForTurnTimeField(
  turnTime: ReduxFormFields<TableBasedTemplates.TurnTimeField>
): string {
  if (!isEmpty(turnTime.maxPartySize.value)) {
    return `${turnTime.minPartySize.value}-${turnTime.maxPartySize.value}`;
  } else {
    return `${turnTime.minPartySize.value}+`;
  }
}

/**
 * Given a list of turn times for a ticket type , creates an array of objects
 * that are the right shape to be an initialValue in a redux form that uses TurnTimeForm
 * @returns A list of objects in the right shape for TurnTimeForm
 */
export function constructTurnTimeFieldsForTicketType(
  turnTimesForTicketType: dashboard.TurnTime[]
): TableBasedTemplates.StartTimeAndTurnTimes[] {
  return map(
    Object.values(groupBy(turnTimesForTicketType, (turnTime) => turnTime.startingAtTime)),
    (singlePartySizeTurnTimes, idx) => {
      const sortedPartySizes = sortBy(
        singlePartySizeTurnTimes,
        (turnTime) => getRange(turnTime).min
      );
      // Always force the first one to be 04:00, in case data gets bad
      const startTime =
        idx === 0 ? AppSettings.LAST_TIME_IN_BUSINESS_DAY : sortedPartySizes[0].startingAtTime;
      return {
        startTime,
        turnTimes: map(sortedPartySizes, (turnTime) => {
          const range = getRange(turnTime);
          return {
            expectedTurnTimeMinutes: toString(turnTime.expectedTurnTimeMinutes),
            minPartySize: toString(range.min),
            maxPartySize: range.hasUnboundMax() ? '' : toString(range.max),
          };
        }),
      };
    }
  );
}

/**
 * Returns an object in the shape needed by TurnTimesForm that's filled
 * with the default values. Can be used to construct a new field
 * when an experience override is created.
 */
export function getDefaultTurnTimeFields(
  defaultTurnTimes = TURN_DEFAULT_PARTY_SIZES
): TableBasedTemplates.StartTimeAndTurnTimes[] {
  return [
    {
      startTime: AppSettings.LAST_TIME_IN_BUSINESS_DAY,
      turnTimes: map(
        defaultTurnTimes,
        ({ minPartySize, maxPartySize, expectedTurnTimeMinutes }) => {
          return {
            expectedTurnTimeMinutes,
            minPartySize,
            maxPartySize,
          };
        }
      ),
    },
  ];
}

export function getRange(turnTime: dashboard.TurnTime): NonemptyClosedNumericRange {
  if (turnTime.isNewStyle) {
    if (has(turnTime, 'maxPartySize')) {
      return tockRange(turnTime.minPartySize, turnTime.maxPartySize);
    } else {
      return tockRange(turnTime.minPartySize, undefined);
    }
  } else {
    const size = turnTime.minPartySize;
    if (size === 2) {
      return tockRange(1, 2);
    } else if (size === 4) {
      return tockRange(3, 5);
    } else if (size === 6) {
      return tockRange(6, undefined);
    } else {
      throw new Error('Bad party size for old-style turn time');
    }
  }
}

/**
 * Finds the relevant turn time given a party size and ticket type id.
 * At the moment, we provide three different hard-coded turn time party sizes (2, 4, and 6).
 * As a result, we want to map sizes 1-2 to turn time w/ party size 2, 3-5 to turn time w/ party size 4,
 * and 6+ to turn time w/ party size 6.
 * In addition, if we can't find the relevant turn time, we fall back onto a default (if one is configured).
 * Defaults are identifiable by their lack of a ticketTypeId.
 *
 * @param turnTimes A map of existing turn times
 * @param sizeToConsider The partySize for which we are looking for a turn time
 * @param ticketTypeId The ticket type id for which we are looking for a turn time
 * @returns The relevant turn time or undefined
 */
export function findRelevantTurnTime(
  turnTimes: TableBasedTemplates.TurnTimes,
  turnTimeRequest: TurnTimeRequest
): dashboard.TurnTime | undefined {
  if (isOrderVariety(turnTimeRequest.variety)) {
    return undefined;
  }
  return (
    lookupTimeForSizeAndId(
      turnTimes,
      turnTimeRequest.ticketTypeId,
      turnTimeRequest.partySize,
      turnTimeRequest.startDateTime
    ) ||
    lookupTimeForSizeAndId(
      turnTimes,
      NO_TICKET_TYPE_ID_FOR_TURN,
      turnTimeRequest.partySize,
      turnTimeRequest.startDateTime
    )
  );
}

function lookupTimeForSizeAndId(
  turnTimes: TableBasedTemplates.TurnTimes,
  ticketTypeId: number | undefined,
  partySize: number,
  startDateTime: LocalDateTime
): dashboard.TurnTime | undefined {
  const turnTimesForType = turnTimes[ticketTypeId || NO_TICKET_TYPE_ID_FOR_TURN];
  if (!turnTimesForType) {
    return undefined;
  }
  const startTimeToTurnTime = turnTimesForType.get(partySize);
  return startTimeToTurnTime && startTimeToTurnTime[startDateTime.toLocalTime().toString()];
}

/**
 * For a given ticket type and min/max party size range and a given turn time
 * map, returns a map from every start time to the shortest possible turn time
 * in the map for that time.
 *
 * @param turnTimes turn times to analyze
 * @param minPartySize smallest party size that could be seated (inclusive)
 * @param maxPartySize largest party size that could be seated (inclusive)
 * @param ticketTypeId ticket type, or undefined for default / walkins
 * @returns map from start time (formatted as LocalDate.toString()) to expected
 *     number of minutes for the fastest party size to turn at that time.
 */
export function getStartTimeToMinimumTurnTimeMap(
  turnTimes: TableBasedTemplates.TurnTimes,
  minPartySize: number,
  maxPartySize: number,
  ticketTypeId?: number
): { [startTime: string]: number } {
  const fullRangeMap = turnTimes[ticketTypeId || NO_TICKET_TYPE_ID_FOR_TURN];
  if (!fullRangeMap) {
    return {};
  }
  const relevantRangeMap = fullRangeMap.restrictToRange(tockRange(minPartySize, maxPartySize));
  const possibleTurnTimesByTime = relevantRangeMap.values();
  const resultMap: { [startTime: string]: number } = {};
  forEach(possibleTurnTimesByTime, (map) => {
    forEach(map, (value, key) => {
      const newExpectedTurnTimeValueMinutes = value.expectedTurnTimeMinutes;
      const existingValue = resultMap[key];
      if (!existingValue || newExpectedTurnTimeValueMinutes < existingValue) {
        resultMap[key] = newExpectedTurnTimeValueMinutes;
      }
    });
  });
  return resultMap;
}

export interface TurnTimeInfoRequest {
  startDateTime: LocalDateTime;
  ticketTypeId: number;
  maxPurchaseSize: number;
  minPurchaseSize: number;
}

/* Runs getTurnTimeMinutes with the max function */
export function getMaxTurnTimeMinutes(
  turnTimes: TableBasedTemplates.TurnTimes,
  request: TurnTimeInfoRequest
): number {
  return getTurnTimeMinutes(turnTimes, request, max);
}
/* Runs getTurnTimeMinutes with the min function */
export function getMinTurnTimeMinutes(
  turnTimes: TableBasedTemplates.TurnTimes,
  request: TurnTimeInfoRequest
): number {
  return getTurnTimeMinutes(turnTimes, request, min);
}

/**
 * Determines the maximum turn time (in minutes) for the provided ticket group information
 *
 * @param turnTimes A map of existing turn times
 * @param turnTimeInfoRequest The ticketTypeId, maxPurchaseSize, and minPurchaseSize of the ticket group
 * @returns The relevant turn time in minutes
 */
export function getTurnTimeMinutes(
  turnTimes: TableBasedTemplates.TurnTimes,
  turnTimeInfoRequest: TurnTimeInfoRequest,
  optimizingFunction: (turnTimes: number[]) => number | undefined
): number {
  const possibleTurnTimes = map(
    getPossibleTurnTimes(turnTimes, turnTimeInfoRequest),
    (turnTime: dashboard.TurnTime) => turnTime.expectedTurnTimeMinutes
  );
  return optimizingFunction(possibleTurnTimes) || 15;
}

/**
 * Fetches all possible turn times for the relevant turn time info request
 *
 * @param turnTimes A map of existing turn times
 * @param turnTimeInfoRequest The ticketTypeId, maxPurchaseSize, and minPurchaseSize of the ticket group
 * @returns a list of the turn time objects
 */
export function getPossibleTurnTimes(
  turnTimes: TableBasedTemplates.TurnTimes,
  { ticketTypeId, startDateTime, maxPurchaseSize, minPurchaseSize }: TurnTimeInfoRequest
): dashboard.TurnTime[] {
  // Explicit number conversion is needed because as it turns out, there's the possibility for non-numbers to be passed in here (think Formik fields that are supposed to be numbers but are passed in as strings)
  const possibleBookingSizes = range(Number(minPurchaseSize), Number(maxPurchaseSize) + 1);
  const turnTimeUniqueIds: Set<number> = new Set();
  const possibleTurnTimes = possibleBookingSizes.reduce<dashboard.TurnTime[]>((acc, partySize) => {
    const relevantTurnTime = findRelevantTurnTime(turnTimes, {
      partySize,
      startDateTime,
      ticketTypeId,
    });
    if (relevantTurnTime !== undefined && !turnTimeUniqueIds.has(relevantTurnTime.id)) {
      turnTimeUniqueIds.add(relevantTurnTime.id);
      acc.push(relevantTurnTime);
    }

    return acc;
  }, []);

  return possibleTurnTimes;
}

/**
 * Massages the values from the form in the TurnTimesForm into the shape
 * of a TurnTime proto, so that it can be sent to the server.
 *
 * @param experienceFields Redux form field values
 * @returns An array of turn times
 */
export function massageFormDataIntoTurnTimes(
  experienceFields: TableBasedTemplates.ExperienceTurnTimeValues[],
  businessId: number
): Array<Partial<dashboard.TurnTime>> {
  return flatMap(experienceFields, (experienceInput) => {
    return flatten(
      massageTurnTimeFields(experienceInput.startTimes, businessId, experienceInput.id)
    );
  });
}

/**
 * Massages the values from the form in the TurnTimesForm or AddTemplateRoute into the shape
 * of a TurnTime proto, so that it can be sent to the server.
 *
 * @param defaultTurnFields List of default turn time fields
 * @returns An array of turn times
 */
export function massageTurnTimeFields(
  startTimeAndTurnTimesArr: TableBasedTemplates.StartTimeAndTurnTimes[],
  businessId: number,
  experienceId?: number
): Array<Partial<dashboard.TurnTime>> {
  return flatMap(
    startTimeAndTurnTimesArr,
    (startTimeAndTurnTimes: TableBasedTemplates.StartTimeAndTurnTimes) => {
      // For the moment, assuming this is new style turn times if any have a range.
      // YK 2019-03-18: This may not be necessary at all and it can always be true, but this is a sanity check
      const isNewStyle = some(startTimeAndTurnTimes.turnTimes, (turnTime) => {
        return turnTime.minPartySize !== turnTime.maxPartySize;
      });
      return map(startTimeAndTurnTimes.turnTimes, (turnTime) => {
        const newTurnTime: Partial<dashboard.TurnTime> = {
          businessId,
          expectedTurnTimeMinutes: toNumber(turnTime.expectedTurnTimeMinutes),
          maxPartySize: isEmpty(turnTime.maxPartySize)
            ? undefined
            : toNumber(turnTime.maxPartySize),
          minPartySize: toNumber(turnTime.minPartySize),
          startingAtTime: startTimeAndTurnTimes.startTime,
          isNewStyle,
        };
        if (experienceId) {
          newTurnTime.ticketTypeId = experienceId;
        }
        return newTurnTime;
      });
    }
  );
}

/**
 * Creates a new template info. Defaults most of the selection to what was most recently created, so
 * that repeat adds don't require constant adjustment and tweaking.
 *
 * @param timeString The time the templateInfo should be inserted
 * @param ticketTypeId The ticketTypeId to use
 * @param previousTemplateInfo The most recently created template info
 * @returns A new template info
 */
export function createNewSeatingTemplateInfo(
  timeString: string,
  ticketTypeId: number,
  previousTemplateInfo?: dashboard.SeatingTemplateInfo
): Partial<dashboard.SeatingTemplateInfo> {
  const relevantFromPreviousInfo = pick(previousTemplateInfo, [
    'allowsStragglers',
    'heldTickets',
    'isEvensOnly',
    'maxPurchaseSize',
    'maxSize',
    'minPurchaseSize',
    'variety',
  ]);
  return defaults(relevantFromPreviousInfo, {
    allowsStragglers: false,
    heldTickets: 0,
    isEvensOnly: false,
    maxPurchaseSize: 2,
    maxSize: 2,
    minPurchaseSize: 2,
    startTime: timeString,
    ticketTypeId,
    variety: TableTypeConstants.TABLE,
  });
}

/**
 * Creates a new template info. Defaults the experience and minPurchaseSize to what whatever values
 * were most recently added to the template. Other size values, however, are taken from the table
 * itself, to handle jumping between tables (and also on the assumption that over-sizing a table is rare).
 *
 * @param timeString The time the templateInfo should be inserted
 * @param tableOrTableGroup Information about the table or table group for which this template info is being created
 * @param fallbackTicketTypeId The ticketTypeId to use if none is provided from the previous templateInfo
 * @param previousTemplateInfo The most recently created template info
 * @returns A new template info
 */
export function createNewSeatingTemplateInfoForTable(
  timeString: string,
  tableOrTableGroup: TableBasedTemplates.TableOrTableGroup,
  ticketTypeId: number
): Partial<dashboard.SeatingTemplateInfo> {
  const { id, size, type } = tableOrTableGroup;

  const newTemplateInfo: Partial<dashboard.SeatingTemplateInfo> = {
    allowsStragglers: false,
    isEvensOnly: false,
    maxPurchaseSize: size,
    maxSize: size,
    minPurchaseSize: size,
    startTime: timeString,
    ticketTypeId,
    variety: TableTypeConstants.TABLE,
  };
  if (type === 'GROUP') {
    newTemplateInfo.tableGroupId = id;
  } else {
    newTemplateInfo.tableId = id;
  }

  return newTemplateInfo;
}

/**
 * Takes a list of template infos and massages them to match the type needed for our scan line algorithms
 * Of particular note is that we need to provide an arbitrary date in order to handle times after midnight.
 * Anything other than December 31st should do.
 * @param someDayString The arbitrary day
 * @param templateInfos the list of template infos
 */
export function massageToInventorySquareDescription(
  templateInfos: dashboard.SeatingTemplateInfo[],
  turnTimesBySize: TableBasedTemplates.TurnTimes,
  someDayString = '2022-01-01'
) {
  return map(templateInfos, (templateInfo) => {
    const startDateTime = adjustForBusinessDay(someDayString, templateInfo.startTime);
    const relevantTurnTime = findRelevantTurnTime(turnTimesBySize, {
      partySize: templateInfo.maxPurchaseSize,
      startDateTime,
      ticketTypeId: templateInfo.ticketTypeId || NO_TICKET_TYPE_ID_FOR_TURN,
    });
    const expectedTurnTimeMinutes = oc(relevantTurnTime).expectedTurnTimeMinutes(0);
    const anticipatedEndDateTime = startDateTime.plusMinutes(expectedTurnTimeMinutes);
    return {
      ...templateInfo,
      anticipatedEndDateTime,
      startDateTime,
    };
  });
}

/**
 * finds only the related ids for a given template info id
 * @param id the template info id
 * @param templateInfos the array of template infos to search
 */
export function getRelatedInfoIds(id: number, templateInfos: dashboard.SeatingTemplateInfo[]) {
  const templateInfoToFindRelated = find(templateInfos, {
    id,
  });

  if (!templateInfoToFindRelated) {
    return [];
  }

  const entries = filter(templateInfos, (templateInfo) => {
    if (templateInfoToFindRelated.startTime !== templateInfo.startTime) {
      return false;
    }
    if (templateInfoToFindRelated.variety === TableTypeConstants.COMMUNAL_TABLE) {
      return templateInfoToFindRelated.ticketTypeId === templateInfo.ticketTypeId;
    } else {
      if (
        templateInfoToFindRelated.ticketTypeId !== templateInfo.ticketTypeId ||
        templateInfoToFindRelated.maxSize !== templateInfo.maxSize ||
        templateInfoToFindRelated.minPurchaseSize !== templateInfo.minPurchaseSize ||
        templateInfoToFindRelated.maxPurchaseSize !== templateInfo.maxPurchaseSize ||
        templateInfoToFindRelated.variety !== templateInfo.variety ||
        templateInfoToFindRelated.allowsStragglers !== templateInfo.allowsStragglers ||
        templateInfoToFindRelated.isEvensOnly !== templateInfo.isEvensOnly
      ) {
        return false;
      }
      return true;
    }
  });

  return map(entries, (entry) => entry.id);
}

export function removeRelatedInfoIds(
  id: number,
  selectedInfoIds: number[],
  templateInfos: dashboard.SeatingTemplateInfo[]
) {
  const templateInfoToFindRelated = find(templateInfos, {
    id,
  });

  if (!templateInfoToFindRelated) {
    return selectedInfoIds;
  }

  const relatedIds = getRelatedInfoIds(id, templateInfos);

  return filter(selectedInfoIds, (selectedInfoId) => !includes(relatedIds, selectedInfoId));
}
